import { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetMeusPedidosQuery, useGetPedidoStatusQuery } from '../api';
import type { RootState } from '../store';
import { marcarPedidoLocalFinalizado, pedidoNaoEncontrado, removerPedidoLocal, usePedidosLocais } from '../lib/pedidos-locais';
import { pedidoFinalizado, resumoAcompanhamento } from '../lib/acompanhamento-pedido';

/** Fora da tela de acompanhamento não há socket para quem não tem conta: o status chega por polling lento. */
const POLLING_MS = 30_000;

const pulso = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(1.9); opacity: 0; }
`;

/**
 * Faixa no topo do cardápio de DELIVERY avisando que há um pedido em andamento, com
 * o status ao vivo e o atalho para o acompanhamento. Some quando o pedido termina
 * (entregue, cancelado ou falha). Sem login, os pedidos vêm do aparelho
 * (`lib/pedidos-locais`); logado, de "Meus pedidos".
 */
export function PedidoEmAndamento({ idConfiguracao }: { idConfiguracao: number }): JSX.Element | null {
  const navigate = useNavigate();
  const logado = Boolean(useSelector((s: RootState) => s.auth.accessToken));
  const pedidosLocais = usePedidosLocais(idConfiguracao);
  const { data: meusPedidos } = useGetMeusPedidosQuery(1, { skip: !logado });
  // Finalizados vistos nesta sessão: a lista de "Meus pedidos" pode estar em cache com o status antigo.
  const [encerrados, setEncerrados] = useState<ReadonlySet<string>>(new Set());

  const candidatos = useMemo(() => {
    const ids = logado
      ? (meusPedidos?.itens ?? [])
          .filter((p) => p.origem === 'DELIVERY' && !pedidoFinalizado(p.situacao))
          .map((p) => p.id)
      : pedidosLocais.filter((p) => !p.finalizado).map((p) => p.id);
    return ids.filter((id) => !encerrados.has(id));
  }, [logado, meusPedidos, pedidosLocais, encerrados]);

  const idAtual = candidatos[0];
  const { data: pedido, error } = useGetPedidoStatusQuery(idAtual ?? '', {
    skip: !idAtual,
    pollingInterval: POLLING_MS,
    refetchOnMountOrArgChange: true,
  });

  const naoEncontrado = pedidoNaoEncontrado(error);
  const pedidoAtual = pedido && pedido.id === idAtual ? pedido : undefined;
  const acabou = pedidoAtual != null && pedidoFinalizado(pedidoAtual.situacao);

  useEffect(() => {
    if (!idAtual || (!acabou && !naoEncontrado)) return;
    setEncerrados((atual) => new Set(atual).add(idAtual));
    if (logado) return;
    if (naoEncontrado) removerPedidoLocal(idConfiguracao, idAtual);
    else marcarPedidoLocalFinalizado(idConfiguracao, idAtual);
  }, [idAtual, acabou, naoEncontrado, logado, idConfiguracao]);

  if (!pedidoAtual || acabou) return null;

  const { rotulo, aguardando } = resumoAcompanhamento(pedidoAtual);
  const ehRetirada = pedidoAtual.payloadJson?.tipoDelivery === 'RETIRAR_LOCAL';
  const codigo = pedidoAtual.shortId ?? pedidoAtual.id.slice(0, 8).toUpperCase();
  // Não pago ainda não está "em andamento": a faixa chama para pagar.
  const faltaPagar = pedidoAtual.situacao === 'AGUARDANDO_PAGAMENTO';
  const acao = faltaPagar ? 'Pagar' : 'Acompanhar';

  return (
    <ButtonBase
      onClick={() => navigate(`/pedido/${pedidoAtual.id}`)}
      aria-label={`${acao} pedido ${codigo}: ${rotulo}`}
      sx={(tema) => ({
        width: '100%',
        mb: 3,
        px: 2,
        py: 1.5,
        gap: 1.75,
        justifyContent: 'flex-start',
        textAlign: 'left',
        borderRadius: 2,
        border: 1,
        borderColor: alpha(tema.palette.primary.main, 0.28),
        bgcolor: 'background.paper',
        backgroundImage: `linear-gradient(90deg, ${alpha(tema.palette.primary.main, 0.1)}, ${alpha(tema.palette.primary.main, 0.02)})`,
        boxShadow: `0 6px 18px ${alpha(tema.palette.primary.main, 0.12)}`,
        transition: tema.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
          borderColor: alpha(tema.palette.primary.main, 0.5),
          boxShadow: `0 8px 22px ${alpha(tema.palette.primary.main, 0.18)}`,
        },
      })}
    >
      <Box sx={{ position: 'relative', flexShrink: 0, width: 44, height: 44 }}>
        {aguardando && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              animation: `${pulso} 1.8s ease-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
            }}
          />
        )}
        <Box
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          {ehRetirada ? <StorefrontIcon /> : <DeliveryDiningIcon />}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="primary.main"
          sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', lineHeight: 1.4 }}
        >
          {faltaPagar ? 'Falta pagar' : 'Pedido em andamento'}
        </Typography>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {rotulo}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          Pedido #{codigo}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', flexShrink: 0 }}>
        <Typography variant="button" sx={{ display: { xs: 'none', sm: 'block' }, textTransform: 'none', fontWeight: 600 }}>
          {acao}
        </Typography>
        <ChevronRightIcon />
      </Box>
    </ButtonBase>
  );
}
